import { useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { ActionIcon, Button, Group, Menu, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconArrowBackUp, IconLayoutGrid, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import {
  contourGeometry,
  formatQuantity,
  rectangleContour,
  WALL_DIRECTIONS,
  type MeasureWall,
  type WallDirection,
} from '@erp/shared';
import { CONFLICT, DIM, FLOOR, FONT, INK, fmtMeters } from './RoomDrawings';

type Point = [number, number];

/** Шаг привязки длины при рисовании, м. Точный размер прораб впишет в список стен. */
const SNAP = 0.05;
/** Касание ближе этого к первой точке замыкает контур, м. */
const CLOSE_RADIUS = 0.35;
/** Поле для рисования, м: с запасом на большую комнату. */
const FIELD_W = 12;
const FIELD_H = 8;

const DIRECTION_LABELS: Record<WallDirection, string> = {
  0: '→ вправо',
  45: '↘ вправо-вниз',
  90: '↓ вниз',
  135: '↙ влево-вниз',
  180: '← влево',
  225: '↖ влево-вверх',
  270: '↑ вверх',
  315: '↗ вправо-вверх',
};
const DIRECTION_OPTIONS = WALL_DIRECTIONS.map((d) => ({ value: String(d), label: DIRECTION_LABELS[d] }));

function unit(direction: number): Point {
  const rad = (direction * Math.PI) / 180;
  return [Math.cos(rad), Math.sin(rad)];
}

function snapDirection(dx: number, dy: number): WallDirection {
  const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  return (((Math.round(deg / 45) * 45) % 360) + 360) % 360 as WallDirection;
}

/**
 * Следующий угол контура: направление от предыдущего угла привязывается к 45°,
 * длина — к 5 см. Так от руки, пальцем на телефоне, получаются ровные стены.
 */
function snapSegment(from: Point, cursor: Point): { point: Point; direction: WallDirection; length: number } | null {
  const dx = cursor[0] - from[0];
  const dy = cursor[1] - from[1];
  if (Math.hypot(dx, dy) < SNAP) return null;
  const direction = snapDirection(dx, dy);
  const [ux, uy] = unit(direction);
  const length = Math.round((dx * ux + dy * uy) / SNAP) * SNAP;
  if (length < SNAP) return null;
  return { point: [from[0] + ux * length, from[1] + uy * length], direction, length };
}

function formatLength(value: number): string {
  return formatQuantity(value.toFixed(2));
}

/**
 * Углы → стены. Контур всегда сохраняется по часовой стрелке: от этого
 * зависит, какая сторона стены внутренняя и откуда отсчитывается «от угла»
 * у проёмов. Нарисованный против часовой обход разворачивается.
 */
function pointsToWalls(points: Point[]): MeasureWall[] {
  let twice = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i]!;
    const [x2, y2] = points[(i + 1) % points.length]!;
    twice += x1 * y2 - x2 * y1;
  }
  const ordered = twice < 0 ? [...points].reverse() : points;
  return ordered.map((from, i) => {
    const to = ordered[(i + 1) % ordered.length]!;
    const dx = to[0] - from[0];
    const dy = to[1] - from[1];
    const direction = snapDirection(dx, dy);
    const [ux, uy] = unit(direction);
    const length = Math.max(Math.round((dx * ux + dy * uy) / SNAP) * SNAP, SNAP);
    return { length: formatLength(length), direction };
  });
}

// Г-образное помещение 5 × 4 без угла 2 × 2 — самая частая «сложная» форма.
const L_SHAPE: MeasureWall[] = [
  { length: '5', direction: 0 },
  { length: '2', direction: 90 },
  { length: '2', direction: 180 },
  { length: '2', direction: 90 },
  { length: '3', direction: 180 },
  { length: '4', direction: 270 },
];

function DrawingCanvas({
  onDone,
  onCancel,
  extraActions,
}: {
  onDone: (walls: MeasureWall[]) => void;
  onCancel?: () => void;
  extraActions?: ReactNode;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [cursor, setCursor] = useState<Point | null>(null);

  function toWorld(e: PointerEvent<SVGSVGElement>): Point | null {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
    return [p.x, p.y];
  }

  const first = points[0];
  const lastPoint = points.at(-1);
  const segment = lastPoint && cursor ? snapSegment(lastPoint, cursor) : null;
  const canClose = points.length >= 3;
  const nearStart =
    canClose && first && cursor ? Math.hypot(cursor[0] - first[0], cursor[1] - first[1]) <= CLOSE_RADIUS : false;

  function place() {
    if (!cursor) return;
    if (!first) {
      // Первый угол — на узел сетки 0,5 м: контур ровно ложится на клетки.
      setPoints([[Math.round(cursor[0] * 2) / 2, Math.round(cursor[1] * 2) / 2]]);
      return;
    }
    if (nearStart) {
      onDone(pointsToWalls(points));
      return;
    }
    if (segment) setPoints([...points, segment.point]);
  }

  const gridLines = [];
  for (let x = 0; x <= FIELD_W; x += 0.5) {
    gridLines.push(
      <line key={`x${x}`} x1={x} y1={0} x2={x} y2={FIELD_H} stroke={x % 1 === 0 ? '#d9d3c9' : '#ece8e2'} strokeWidth={0.015} />,
    );
  }
  for (let y = 0; y <= FIELD_H; y += 0.5) {
    gridLines.push(
      <line key={`y${y}`} x1={0} y1={y} x2={FIELD_W} y2={y} stroke={y % 1 === 0 ? '#d9d3c9' : '#ece8e2'} strokeWidth={0.015} />,
    );
  }

  const preview = nearStart && first ? first : segment?.point;

  return (
    <Stack gap="xs">
      <Text size="xs" c="dimmed">
        Отмечайте углы помещения по порядку. Стены привязываются к 45°, длины — к 5 см; точные размеры впишете
        после. Чтобы замкнуть контур, коснитесь первой точки. Клетка — 0,5 м.
      </Text>
      <svg
        ref={svgRef}
        viewBox={`-0.3 -0.3 ${FIELD_W + 0.6} ${FIELD_H + 0.6}`}
        width="100%"
        // Без touch-action палец прокручивал бы страницу вместо рисования.
        style={{ display: 'block', touchAction: 'none', background: '#fff', border: '1px solid #d9d3c9', cursor: 'crosshair' }}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          setCursor(toWorld(e));
        }}
        onPointerMove={(e) => setCursor(toWorld(e))}
        onPointerUp={place}
        onPointerLeave={(e) => e.pointerType === 'mouse' && setCursor(null)}
      >
        {gridLines}
        {points.length >= 2 && (
          <polyline
            points={points.map((p) => p.join(',')).join(' ')}
            fill="none"
            stroke={INK}
            strokeWidth={0.08}
            strokeLinejoin="miter"
          />
        )}
        {canClose && (
          <polygon points={points.map((p) => p.join(',')).join(' ')} fill={FLOOR} opacity={0.6} />
        )}
        {lastPoint && preview && (
          <line
            x1={lastPoint[0]}
            y1={lastPoint[1]}
            x2={preview[0]}
            y2={preview[1]}
            stroke={nearStart ? INK : DIM}
            strokeWidth={0.05}
            strokeDasharray="0.15 0.1"
          />
        )}
        {lastPoint && segment && !nearStart && (
          <text
            x={(lastPoint[0] + segment.point[0]) / 2}
            y={(lastPoint[1] + segment.point[1]) / 2 - 0.15}
            fontSize={0.32}
            fontFamily={FONT}
            textAnchor="middle"
            fill={INK}
            paintOrder="stroke"
            stroke="#fff"
            strokeWidth={0.08}
          >
            {formatLength(segment.length)} м
          </text>
        )}
        {points.map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={i === 0 && canClose ? 0.16 : 0.08}
            fill={i === 0 && nearStart ? CONFLICT : INK}
          />
        ))}
        {!first && cursor && <circle cx={Math.round(cursor[0] * 2) / 2} cy={Math.round(cursor[1] * 2) / 2} r={0.08} fill={DIM} />}
      </svg>
      <Group gap="xs">
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconArrowBackUp size={14} />}
          disabled={points.length === 0}
          onClick={() => setPoints(points.slice(0, -1))}
        >
          Отменить точку
        </Button>
        <Button size="compact-xs" disabled={!canClose} onClick={() => onDone(pointsToWalls(points))}>
          Замкнуть контур
        </Button>
        {extraActions}
        {onCancel && (
          <Button size="compact-xs" variant="subtle" onClick={onCancel}>
            Отмена
          </Button>
        )}
      </Group>
    </Stack>
  );
}

/**
 * Контур помещения сложной формы: рисование от руки по сетке и список стен с
 * точными длинами. Площадь и периметр считаются по контуру в @erp/shared —
 * теми же функциями, что и на сервере.
 */
export function ContourEditor({
  contour,
  rectLength,
  rectWidth,
  canEdit,
  onChange,
}: {
  contour: MeasureWall[] | null | undefined;
  /** Размеры из прямоугольного режима — для шаблона «Прямоугольник». */
  rectLength: string;
  rectWidth: string;
  canEdit: boolean;
  onChange: (contour: MeasureWall[]) => void;
}) {
  const walls = contour ?? [];
  const [drawing, setDrawing] = useState(walls.length < 3);

  function apply(next: MeasureWall[]) {
    onChange(next);
    setDrawing(false);
  }

  const templates = (
    <Menu position="bottom-start">
      <Menu.Target>
        <Button size="compact-xs" variant="light" leftSection={<IconLayoutGrid size={14} />}>
          Шаблон
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => apply(rectangleContour(rectLength || '4', rectWidth || '3'))}>
          Прямоугольник
        </Menu.Item>
        <Menu.Item onClick={() => apply(L_SHAPE)}>Г-образное</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  if (canEdit && (drawing || walls.length < 3)) {
    return (
      <DrawingCanvas
        onDone={apply}
        onCancel={walls.length >= 3 ? () => setDrawing(false) : undefined}
        extraActions={templates}
      />
    );
  }

  const geometry = contourGeometry(walls);
  const updateWall = (index: number, patch: Partial<MeasureWall>) =>
    onChange(walls.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  return (
    <Stack gap={6}>
      {walls.map((wall, index) => (
        <Group key={index} gap="xs" wrap="nowrap" align="center">
          <Text size="sm" c="dimmed" w={22} ta="right" className="erp-tabular">
            {index + 1}
          </Text>
          <TextInput
            size="xs"
            inputMode="decimal"
            value={wall.length}
            onChange={(e) => updateWall(index, { length: e.currentTarget.value })}
            disabled={!canEdit}
            rightSection={<Text size="xs" c="dimmed">м</Text>}
            w={96}
            aria-label={`Длина стены ${index + 1}`}
          />
          <Select
            size="xs"
            data={DIRECTION_OPTIONS}
            value={String(wall.direction)}
            onChange={(v) => v && updateWall(index, { direction: Number(v) as WallDirection })}
            disabled={!canEdit}
            allowDeselect={false}
            w={150}
            aria-label={`Направление стены ${index + 1}`}
          />
          {canEdit && (
            <>
              <ActionIcon
                variant="subtle"
                onClick={() => onChange([...walls.slice(0, index + 1), { ...wall }, ...walls.slice(index + 1)])}
                aria-label="Добавить стену после этой"
                title="Добавить стену после этой"
              >
                <IconPlus size={14} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                disabled={walls.length <= 3}
                onClick={() => onChange(walls.filter((_, i) => i !== index))}
                aria-label="Удалить стену"
              >
                <IconTrash size={14} />
              </ActionIcon>
            </>
          )}
        </Group>
      ))}

      {!geometry.closed && (
        <Text size="xs" c="seal.6">
          Контур не замкнут: конец последней стены не дошёл до начала первой на{' '}
          {fmtMeters(Math.abs(geometry.end[0].toNumber()))} м по горизонтали и{' '}
          {fmtMeters(Math.abs(geometry.end[1].toNumber()))} м по вертикали. Проверьте длины стен — площадь
          посчитана с замыканием по прямой.
        </Text>
      )}

      {canEdit && (
        <Group gap="xs">
          <Button size="compact-xs" variant="light" leftSection={<IconPencil size={14} />} onClick={() => setDrawing(true)}>
            Нарисовать заново
          </Button>
          {templates}
        </Group>
      )}
    </Stack>
  );
}
